import { Component } from '@theme/component';

/**
 * Promo video with mute toggle and pause-state CTA overlay.
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement} video
 * @property {HTMLButtonElement} soundToggle
 * @property {HTMLAnchorElement} [cta]
 * @property {HTMLTemplateElement} [sources]
 *
 * @extends {Component<Refs>}
 */
class MgPromoVideoComponent extends Component {
  requiredRefs = ['video', 'soundToggle'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {MediaQueryList | undefined} */
  #reducedMotion = undefined;

  /** @type {IntersectionObserver | null} */
  #playbackObserver = null;

  /** Shopper paused; scrolling back into view must not restart playback. */
  #pausedByShopper = false;

  /** Playback has started, so the pause-state CTA can show. */
  #hasPlayed = false;

  /** Paused state at pointerdown, before native controls toggle playback. */
  #wasPausedBeforePointer = false;

  connectedCallback() {
    super.connectedCallback();

    const { video } = this.refs;
    if (!(video instanceof HTMLVideoElement)) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion.addEventListener('change', this.#onReducedMotionChange, { signal });

    const startMuted =
      this.dataset.autoplay !== 'false' ? true : this.dataset.startMuted !== 'false';
    video.muted = startMuted;
    if (startMuted) video.volume = 1;

    this.#syncSoundLabel();
    this.#syncPausedState();

    video.addEventListener('play', this.#onPlay, { signal });
    video.addEventListener('pause', this.#syncPausedState, { signal });
    video.addEventListener('volumechange', this.#syncSoundLabel, { signal });
    video.addEventListener('pointerdown', this.#onVideoPointerDown, { signal });
    video.addEventListener('click', this.#onVideoClick, { signal });

    if (this.dataset.autoplay === 'false') {
      this.#syncPausedState();
      return;
    }

    if (this.#prefersReducedMotion) {
      video.pause();
      this.#syncPausedState();
    }

    this.#watchForPlayback();
  }

  disconnectedCallback() {
    this.#playbackObserver?.disconnect();
    this.#playbackObserver = null;
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * @param {Event} event
   */
  toggleSound = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const { video } = this.refs;
    if (!(video instanceof HTMLVideoElement)) return;

    video.muted = !video.muted;
    if (!video.muted && video.volume === 0) video.volume = 1;

    this.#pausedByShopper = false;
    this.#startAutoplay({ force: true });
    this.#syncSoundLabel();
    this.#syncPausedState();
  };

  #syncSoundLabel = () => {
    const { video, soundToggle } = this.refs;
    if (!(video instanceof HTMLVideoElement) || !(soundToggle instanceof HTMLElement)) return;

    const soundOnLabel = this.dataset.soundOnLabel || '';
    const soundOffLabel = this.dataset.soundOffLabel || '';
    const isMuted = video.muted;
    const label = isMuted ? soundOnLabel : soundOffLabel;

    if (label) soundToggle.textContent = label;
    soundToggle.setAttribute('aria-pressed', isMuted ? 'false' : 'true');
    if (label) soundToggle.setAttribute('aria-label', label);
  };

  #onPlay = () => {
    const { video } = this.refs;
    if (!(video instanceof HTMLVideoElement)) return;

    if (this.#pausedByShopper) {
      video.pause();
      return;
    }

    this.#syncPausedState();
  };

  #syncPausedState = () => {
    const { video } = this.refs;
    if (!(video instanceof HTMLVideoElement)) return;

    if (!video.paused) this.#hasPlayed = true;

    const showPausedUi =
      video.paused &&
      (this.dataset.autoplay === 'false' || this.#hasPlayed || this.#prefersReducedMotion);

    this.classList.toggle('is-paused', showPausedUi);
  };

  /**
   * Click/tap video to pause or play — no new visible control (WCAG 2.2.2).
   * @param {MouseEvent} event
   */
  #onVideoPointerDown = () => {
    const { video } = this.refs;
    if (!(video instanceof HTMLVideoElement)) return;
    this.#wasPausedBeforePointer = video.paused;
  };

  #onVideoClick = (event) => {
    const { video } = this.refs;
    if (!(video instanceof HTMLVideoElement)) return;
    if (event.target !== video) return;

    // Native controls pause or play before this click listener runs.
    // Toggling again would undo that pause about a second later, once HLS resumes.
    if (video.controls && video.paused !== this.#wasPausedBeforePointer) {
      this.#pausedByShopper = video.paused;
      this.#syncPausedState();
      return;
    }

    if (video.paused) {
      this.#pausedByShopper = false;
      this.#startAutoplay({ force: true });
    } else {
      this.#pausedByShopper = true;
      video.pause();
    }
    this.#syncPausedState();
  };

  /**
   * Attach media and play only once the section is near the viewport.
   * Sources stay in a template until then so the 1080p stream does not start on load.
   */
  #watchForPlayback() {
    const { video } = this.refs;
    if (!(video instanceof HTMLVideoElement)) return;

    if (!('IntersectionObserver' in window)) {
      this.#startAutoplay({ force: true });
      return;
    }

    this.#playbackObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#startAutoplay();
      },
      { rootMargin: '200px 0px', threshold: 0 }
    );
    this.#playbackObserver.observe(this);
  }

  #attachSources() {
    const { video, sources } = this.refs;
    if (!(video instanceof HTMLVideoElement)) return;
    if (video.querySelector('source')) return;
    if (!(sources instanceof HTMLTemplateElement)) return;

    video.append(sources.content.cloneNode(true));
    video.load();
  }

  /**
   * @param {{ force?: boolean }} [options]
   */
  #startAutoplay({ force = false } = {}) {
    const { video } = this.refs;
    if (!(video instanceof HTMLVideoElement)) return;

    const autoplay = this.dataset.autoplay !== 'false';
    if (!autoplay && !force) return;
    if (this.#pausedByShopper) return;
    if (autoplay && !force && !this.#isNearViewport()) return;

    if (this.#prefersReducedMotion && !force) {
      video.pause();
      this.#syncPausedState();
      return;
    }

    this.#playbackObserver?.disconnect();
    this.#playbackObserver = null;
    this.#attachSources();
    video.preload = 'auto';
    const playback = video.play();
    if (playback && typeof playback.then === 'function') {
      playback
        .then(() => {
          if (this.#pausedByShopper) video.pause();
        })
        .catch(() => {
          this.#syncPausedState();
        });
    }
  }

  #isNearViewport() {
    const rect = this.getBoundingClientRect();
    const margin = 200;
    return rect.bottom >= -margin && rect.top <= window.innerHeight + margin;
  }

  get #prefersReducedMotion() {
    return Boolean(this.#reducedMotion?.matches);
  }

  #onReducedMotionChange = () => {
    const { video } = this.refs;
    if (!(video instanceof HTMLVideoElement)) return;

    if (this.#prefersReducedMotion) {
      video.pause();
    } else if (this.dataset.autoplay !== 'false') {
      this.#startAutoplay();
    }
    this.#syncPausedState();
  };
}

if (!customElements.get('mg-promo-video-component')) {
  customElements.define('mg-promo-video-component', MgPromoVideoComponent);
}
